import { useEffect, useState } from 'react';
import type { AccountFootprint } from '@scribbleplot/shared';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';
import { color } from '../tokens';
import { Button, Eyebrow, TextInput } from '../ui/primitives';

/**
 * Closing an account.
 *
 * Says what will be destroyed before asking, because "delete everything" is not
 * a thing anyone can consent to in the abstract — and boards shared with other
 * people disappear for them too, which is the part someone is most likely not
 * to have considered.
 */
export function DangerZone({ onNotify, onGone }: { onNotify: (message: string) => void; onGone: () => void }) {
  const user = useSession((s) => s.user);
  const [open, setOpen] = useState(false);
  const [footprint, setFootprint] = useState<AccountFootprint | null>(null);
  const [confirm, setConfirm] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .accountFootprint()
      .then(({ footprint: f }) => setFootprint(f))
      .catch(() => setFootprint(null));
  }, [open]);

  const hasPassword = user?.hasPassword ?? true;
  const matches = confirm.trim().toLowerCase() === (user?.email ?? '').toLowerCase();

  const submit = async () => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await api.deleteAccount(confirm.trim(), hasPassword ? password : undefined);
      onGone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not close your account.');
      setBusy(false);
    }
  };

  return (
    <section>
      <Eyebrow>Close account</Eyebrow>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, gap: 12 }}>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>Delete this account</div>
          <div style={{ fontSize: 12, color: color.textDim, marginTop: 2 }}>
            Everything you own goes with it. There is no undo.
          </div>
        </div>
        <Button
          onClick={() => {
            setConfirm('');
            setPassword('');
            setError(null);
            setOpen((was) => !was);
          }}
          hoverStyle={{ background: 'rgba(248,113,113,.08)' }}
          style={{
            height: 30,
            padding: '0 13px',
            borderRadius: 9,
            border: `1px solid rgba(248,113,113,.35)`,
            background: 'transparent',
            color: color.danger,
            fontSize: 12.5,
            fontWeight: 600,
            flex: 'none',
          }}
        >
          {open ? 'Cancel' : 'Delete account'}
        </Button>
      </div>

      {open && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          style={{
            marginTop: 12,
            padding: 14,
            border: `1px solid rgba(248,113,113,.3)`,
            borderRadius: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {footprint && (
            <div style={{ fontSize: 12.5, color: color.textStrong, lineHeight: 1.6 }}>
              This deletes <strong>{footprint.boards}</strong> board{footprint.boards === 1 ? '' : 's'} and{' '}
              <strong>{footprint.images}</strong> uploaded image{footprint.images === 1 ? '' : 's'}.
              {footprint.collaborators > 0 && (
                <>
                  {' '}
                  <strong>{footprint.collaborators}</strong> collaborator
                  {footprint.collaborators === 1 ? '' : 's'} will lose access to{' '}
                  <strong>{footprint.sharedBoards}</strong> shared board
                  {footprint.sharedBoards === 1 ? '' : 's'}.
                </>
              )}{' '}
              Boards other people shared <em>with</em> you are not affected.
            </div>
          )}

          <div style={{ fontSize: 12, color: color.textDim }}>
            Type <strong style={{ color: color.text }}>{user?.email}</strong> to confirm.
          </div>
          <TextInput
            value={confirm}
            onChange={setConfirm}
            placeholder={user?.email ?? 'your email'}
            ariaLabel="Type your email address to confirm"
            name="confirmEmail"
          />

          {hasPassword && (
            <TextInput
              value={password}
              onChange={setPassword}
              placeholder="Your password"
              type="password"
              name="deletePassword"
              autoComplete="current-password"
            />
          )}

          {error && (
            <div role="alert" style={{ fontSize: 12.5, color: color.danger, lineHeight: 1.5 }}>
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy || !matches || (hasPassword && !password)}
            hoverStyle={{ filter: 'brightness(1.1)' }}
            style={{
              alignSelf: 'flex-start',
              height: 34,
              padding: '0 16px',
              borderRadius: 9,
              border: 'none',
              background: color.danger,
              color: '#1A0E0E',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {busy ? 'Closing…' : 'Delete my account permanently'}
          </Button>
        </form>
      )}
    </section>
  );
}
